// @hssna/family-web 设备状态轮询 hook (T4-08)
// 规格: SPEC §8.1 / 战役 4 T4-08
// 后端: S10 Device
//   - GET /v1/devices?include_status=true (含 degradation 字段)
//
// 职责:
//   - 30s 间隔轮询设备状态
//   - 检测 degradation 字段 (battery_low / sensor_fault / connectivity_issue)
//   - 返回 { devices, degradedDevices, refetch }
//
// 实现: 基于 TanStack Query refetchInterval, 自动去重 + 后台刷新

import { useQuery } from '@tanstack/react-query';
import { apiFetch, getFamilyId, unwrap } from '../lib/api';

/** 设备降级类型 */
export type DegradationType =
  | 'battery_low'
  | 'sensor_fault'
  | 'connectivity_issue'
  | 'offline'
  | 'firmware_bug'
  | 'coverage_gap'
  | string;

/** S10 Device (含 degradation 字段, include_status=true 时返回) */
export interface DeviceWithStatus {
  id: string;
  device_sn: string;
  device_type: string;
  model: string;
  vendor: string;
  firmware_version: string;
  family_group_id: string;
  capabilities: string[];
  status: 'online' | 'offline' | 'maintenance' | 'error' | string;
  last_seen?: string;
  battery_level?: number | null;
  signal_strength?: number | null;
  /** 降级标识 (HSSNA 扩展): 当设备存在异常时由 S10 填充 */
  degradation?: {
    type?: DegradationType;
    /** 降级描述 (人类可读) */
    message?: string;
    /** 检测时间 (ISO 8601) */
    detected_at?: string;
    /** 受影响指标列表 (如 hr/bp/spo2) */
    affected_metrics?: string[];
    /** 严重度 1-4 */
    severity?: number;
  } | null;
  created_at?: string;
  updated_at?: string;
}

/** 设备状态轮询结果 */
export interface UseDeviceStatusResult {
  /** 全部设备 (含状态) */
  devices: DeviceWithStatus[];
  /** 处于降级状态的设备 */
  degradedDevices: DeviceWithStatus[];
  /** 是否加载中 (首次) */
  isLoading: boolean;
  /** 错误对象 */
  error: unknown;
  /** 手动重新拉取 */
  refetch: () => void;
}

/** 轮询间隔 (30 秒) */
const POLL_INTERVAL_MS = 30_000;

/**
 * 设备状态轮询 hook
 *
 * - 30s 间隔自动刷新
 * - 自动检测 degradation 字段, 输出 degradedDevices
 * - 仅在 familyId 可用时启动轮询
 *
 * @example
 * const { devices, degradedDevices } = useDeviceStatus();
 * if (degradedDevices.length > 0) {
 *   // 显示降级横幅
 * }
 */
export function useDeviceStatus(): UseDeviceStatusResult {
  const familyId = getFamilyId();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['device-status', familyId],
    queryFn: async () => {
      if (!familyId) return [] as DeviceWithStatus[];
      const params = new URLSearchParams({
        family_group_id: familyId,
        include_status: 'true',
      });
      const res = await apiFetch(`/v1/devices?${params.toString()}`);
      const data = await unwrap<DeviceWithStatus[] | { devices?: DeviceWithStatus[] }>(res);
      if (Array.isArray(data)) return data;
      if (data && typeof data === 'object' && 'devices' in data) {
        return data.devices ?? [];
      }
      return [];
    },
    enabled: !!familyId,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    staleTime: POLL_INTERVAL_MS / 2,
  });

  const devices = data ?? [];
  const degradedDevices = devices.filter(
    (d) => d.degradation != null || d.status === 'offline' || d.status === 'error',
  );

  return {
    devices,
    degradedDevices,
    isLoading,
    error,
    refetch: () => {
      void refetch();
    },
  };
}
